import { useForm } from 'react-hook-form';

import type {
  Address as ColumnAddress,
  AssociatedPerson as ColumnAssociatedPerson,
  BusinessDetails as ColumnBusinessDetails,
  CreateBusinessEntity as ColumnCreateBusinessEntity,
  CreatePersonEntity as ColumnCreatePersonEntity,
  Entity as ColumnEntity,
  LinkAssociatedPersonRequest as ColumnLinkAssociatedPersonRequest,
  PersonDetails as ColumnPersonDetails,
  UpdateAssociatedPersonsRequest as ColumnUpdateAssociatedPersonsRequest,
  UpdateBusinessEntity as ColumnUpdateBusinessEntity,
  UpdatePersonEntity as ColumnUpdatePersonEntity,
} from '@column/api';

export enum EntityFormType {
  Person = 'person',
  Business = 'business',
}

type FormAddress = {
  line1?: ColumnAddress['line_1'] | null;
  line2?: ColumnAddress['line_2'] | null;
  city?: ColumnAddress['city'] | null;
  state?: ColumnAddress['state'] | null;
  postalCode?: ColumnAddress['postal_code'] | null;
  countryCode?: ColumnAddress['country_code'] | null;
};

export type IdentificationDocument = {
  // eslint-disable-next-line id-blacklist
  number?: string;
  countryCode?: string;
};

export interface PersonEntityFormValues {
  id?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  ssn?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: string;
  address?: FormAddress;
  isRoot?: boolean;
  passport?: IdentificationDocument;
  driversLicense?: IdentificationDocument;
  nationalId?: IdentificationDocument;
  countryOfCitizenship?: string;
  pepStatus?: string;
  isHighRisk?: boolean;
  employmentStatus?: string;
  occupation?: string;
  role?: string;
  // Kept as the API's native array types; edited via the comma-separated list inputs.
  sourceOfIncome?: string[];
  income?: number[];
  purposeOfAccount?: string[];
  // Comma-separated list of payment rails; sent verbatim as the API's expected_payment_rails string.
  expectedPaymentRails?: string;
  makesInternationalPayments?: boolean;
  temporaryData?: { isCitizen?: boolean };
}

export interface AssociatedPersonFormValues {
  // Reference to an existing person entity linked to the business as an associated person
  // (mirrors LinkAssociatedPersonRequest).
  personEntityId: string;
  // Cached display name so the picker can label the selection without re-fetching.
  personName?: string;
  roles: string[];
  ownershipPercentage?: number;
  titleInBusiness?: string;
}

export interface BusinessEntityFormValues {
  id?: string;
  businessName?: string;
  ein?: string;
  einPending?: boolean;
  website?: string;
  isRoot?: boolean;
  address?: FormAddress;
  associatedPersons?: AssociatedPersonFormValues[];
  legalType?: string;
  industry?: string;
  dbaName?: string;
  registrationId?: IdentificationDocument;
  countryOfIncorporation?: string;
  stateOfIncorporation?: string;
  dateOfIncorporation?: string;
  yearOfIncorporation?: string;
  description?: string;
  // Kept as the API's native string[]; edited via the comma-separated list input.
  accountUsage?: string[];
  // Business purpose_of_account is a single free-form string in the API.
  purposeOfAccount?: string;
  paymentVolumes?: string;
  // ISO country codes, kept as the API's native string[]; edited via the comma-separated list input.
  countriesOfOperation?: string[];
  sourceOfFunds?: string;
  expectedUseOfFunds?: string;
  // Comma-separated list of payment rails; sent verbatim as the API's expected_payment_rails string.
  expectedPaymentRails?: string;
  makesInternationalPayments?: boolean;
  hasVirtualAddress?: boolean;
  endCustomerType?: string;
  isHighRisk?: boolean;
}

const createEmptyAddress = (): FormAddress => ({
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: '',
});

const createEmptyRequestAddress = (): ColumnAddress => ({
  line_1: '',
  line_2: '',
  city: '',
  state: '',
  postal_code: '',
  country_code: '',
});

const toFormString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return value.toString();
};

const apiAddressToFormAddress = (address?: ColumnAddress): FormAddress | undefined => {
  if (!address) return undefined;
  return {
    line1: address.line_1,
    line2: address.line_2,
    city: address.city,
    state: address.state,
    postalCode: address.postal_code,
    countryCode: address.country_code,
  };
};

const apiIdentificationToFormIdentification = (doc: unknown): IdentificationDocument | undefined => {
  if (!doc || typeof doc !== 'object') return undefined;
  const value = doc as Record<string, unknown>;
  const docNumber = toFormString(value.number);
  const countryCode = toFormString(value.country_code);

  if (!docNumber && !countryCode) return undefined;

  return {
    // eslint-disable-next-line id-blacklist
    number: docNumber,
    countryCode,
  };
};

const apiPersonDetailsToFormValues = (details: ColumnPersonDetails): PersonEntityFormValues => ({
  firstName: details.first_name,
  middleName: details.middle_name,
  lastName: details.last_name,
  ssn: toFormString(details.ssn),
  dateOfBirth: details.date_of_birth,
  email: details.email,
  phoneNumber: details.phone_number,
  address: apiAddressToFormAddress(details.address),
  passport: apiIdentificationToFormIdentification(details.passport),
  driversLicense: apiIdentificationToFormIdentification(details.drivers_license),
  nationalId: apiIdentificationToFormIdentification(details.national_id),
  countryOfCitizenship: details.country_of_citizenship,
  pepStatus: details.pep_status,
  isHighRisk: details.is_high_risk,
  employmentStatus: details.employment_status,
  occupation: details.occupation,
  role: details.role,
  sourceOfIncome: details.source_of_income ? [...details.source_of_income] : undefined,
  income: details.income ? [...details.income] : undefined,
  purposeOfAccount: details.purpose_of_account ? [...details.purpose_of_account] : undefined,
  expectedPaymentRails: details.expected_payment_rails,
  makesInternationalPayments: details.makes_international_payments,
});

// Maps an associated-person record from GET /entities/{id}/associated-persons into a form row.
// This is the only source that carries the linked person's real entity id (person_entity_id);
// business_details.beneficial_owners is PersonDetails data with no id, so it cannot hydrate the
// link. The edit page loads these separately and seeds the associated-persons field from them.
export const associatedPersonRecordToFormValues = (record: ColumnAssociatedPerson): AssociatedPersonFormValues => ({
  personEntityId: record.person_entity_id,
  personName:
    [record.person_details?.first_name, record.person_details?.last_name].filter(Boolean).join(' ').trim() || undefined,
  roles: record.roles ?? [],
  ownershipPercentage: typeof record.ownership_percentage === 'number' ? record.ownership_percentage : undefined,
  titleInBusiness: record.title_in_business ?? undefined,
});

const apiBusinessDetailsToFormValues = (details: ColumnBusinessDetails): BusinessEntityFormValues => ({
  businessName: details.business_name,
  ein: toFormString(details.ein),
  einPending: details.ein_pending ?? false,
  website: details.website,
  address: apiAddressToFormAddress(details.address),
  // Associated persons are not derived from beneficial_owners (that data lacks the linked entity
  // id). The edit page hydrates them from GET /entities/{id}/associated-persons instead.
  legalType: details.legal_type,
  industry: details.industry,
  dbaName: details.dba_name,
  registrationId: apiIdentificationToFormIdentification(details.registration_id),
  countryOfIncorporation: details.country_of_incorporation,
  stateOfIncorporation: details.state_of_incorporation,
  dateOfIncorporation: details.date_of_incorporation,
  yearOfIncorporation: details.year_of_incorporation,
  description: toFormString(details.description),
  // The response returns account_usage as a comma-joined string, though the create/update request
  // (and this form) use a string[] — split it back into the array on load.
  accountUsage: commaStringToList(details.account_usage),
  purposeOfAccount: toFormString(details.purpose_of_account),
  paymentVolumes: toFormString(details.payment_volumes),
  countriesOfOperation: details.countries_of_operation ? [...details.countries_of_operation] : undefined,
  sourceOfFunds: details.source_of_funds,
  expectedUseOfFunds: details.expected_use_of_funds,
  expectedPaymentRails: details.expected_payment_rails,
  makesInternationalPayments: details.makes_international_payments,
  hasVirtualAddress: details.has_virtual_address,
  endCustomerType: details.end_customer_type,
  isHighRisk: details.is_high_risk,
});

const trimValue = (value?: string | null) => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.toString().trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

// Trims each entry and drops the empties from a form string[], returning undefined when nothing is
// left so the request omits the field entirely.
const normalizeStringList = (value?: readonly string[] | null): string[] | undefined => {
  if (!value) return undefined;
  const items = value.map((item) => item.trim()).filter((item) => item.length > 0);
  return items.length > 0 ? items : undefined;
};

// Keeps only the finite numbers from a form number[], returning undefined when none remain.
const normalizeNumberList = (value?: readonly number[] | null): number[] | undefined => {
  if (!value) return undefined;
  const items = value.filter((item) => typeof item === 'number' && Number.isFinite(item));
  return items.length > 0 ? items : undefined;
};

// Splits a comma-joined API string (e.g. business account_usage in a GET response) back into the
// string[] the form uses. Inverse of how the list inputs render an array.
const commaStringToList = (value?: string | null): string[] | undefined => {
  if (value === undefined || value === null) return undefined;
  const items = value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return items.length > 0 ? items : undefined;
};

const normalizeAddress = (address?: FormAddress): ColumnAddress | undefined => {
  if (!address) return undefined;
  const line1 = trimValue(address.line1);
  const line2 = trimValue(address.line2);
  const city = trimValue(address.city);
  const state = trimValue(address.state);
  const postalCode = trimValue(address.postalCode);
  const countryCode = trimValue(address.countryCode);

  if (!line1 && !line2 && !city && !state && !postalCode && !countryCode) return undefined;

  return {
    ...(line1 && { line_1: line1 }),
    ...(line2 && { line_2: line2 }),
    ...(city && { city }),
    ...(state && { state }),
    ...(postalCode && { postal_code: postalCode }),
    ...(countryCode && { country_code: countryCode }),
  } as ColumnAddress;
};

const createEmptyIdentification = (): IdentificationDocument => ({
  // eslint-disable-next-line id-blacklist
  number: '',
  countryCode: '',
});

const normalizeIdentification = (doc?: IdentificationDocument) => {
  if (!doc) return undefined;
  const docNumber = trimValue(doc.number);
  const countryCode = trimValue(doc.countryCode);

  if (!docNumber && !countryCode) return undefined;

  return {
    // eslint-disable-next-line id-blacklist
    ...(docNumber && { number: docNumber }),
    ...(countryCode && { country_code: countryCode }),
  };
};

export const normalizeDateOfBirth = (date: unknown): string | undefined => {
  if (typeof date !== 'string') return undefined;
  const trimmed = date.trim();
  if (!trimmed) return undefined;
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined;
};

const inferCitizenFromIdentifiers = (entry: Partial<PersonEntityFormValues>): boolean => {
  if (entry.ssn && typeof entry.ssn === 'string' && entry.ssn.trim()) {
    return true;
  }
  if (
    (entry.passport && entry.passport.number) ||
    (entry.driversLicense && entry.driversLicense.number) ||
    (entry.nationalId && entry.nationalId.number)
  ) {
    return false;
  }
  return true;
};

export const createDefaultPersonFormValues = (): PersonEntityFormValues => ({
  id: undefined,
  firstName: '',
  middleName: '',
  lastName: '',
  ssn: '',
  dateOfBirth: '',
  email: '',
  phoneNumber: '',
  passport: createEmptyIdentification(),
  driversLicense: createEmptyIdentification(),
  nationalId: createEmptyIdentification(),
  address: createEmptyAddress(),
  countryOfCitizenship: '',
  pepStatus: '',
  isHighRisk: undefined,
  employmentStatus: '',
  occupation: '',
  role: '',
  sourceOfIncome: [],
  income: [],
  purposeOfAccount: [],
  expectedPaymentRails: '',
  makesInternationalPayments: undefined,
  temporaryData: { isCitizen: true },
});

export const createDefaultAssociatedPerson = (): AssociatedPersonFormValues => ({
  personEntityId: '',
  personName: '',
  roles: ['beneficial_owner'],
  ownershipPercentage: undefined,
  titleInBusiness: '',
});

export const createDefaultBusinessFormValues = (): BusinessEntityFormValues => ({
  id: undefined,
  businessName: '',
  ein: '',
  einPending: false,
  website: '',
  address: createEmptyAddress(),
  associatedPersons: [],
  legalType: '',
  industry: '',
  dbaName: '',
  registrationId: createEmptyIdentification(),
  countryOfIncorporation: '',
  stateOfIncorporation: '',
  dateOfIncorporation: '',
  yearOfIncorporation: '',
  description: '',
  accountUsage: [],
  purposeOfAccount: '',
  paymentVolumes: '',
  countriesOfOperation: [],
  sourceOfFunds: '',
  expectedUseOfFunds: '',
  expectedPaymentRails: '',
  makesInternationalPayments: undefined,
  hasVirtualAddress: undefined,
  endCustomerType: '',
  isHighRisk: undefined,
});

export const createInitialBusinessFormState = (): BusinessEntityFormValues => ({
  ...createDefaultBusinessFormValues(),
  // Seed one beneficial owner so the create form opens with the first owner expanded and
  // ready to fill in, instead of an empty list hidden behind an "Add" click. Business
  // entities need at least one beneficial owner, and validation requires its name and date
  // of birth before the entity can be submitted.
  associatedPersons: [createDefaultAssociatedPerson()],
});

const buildPersonUpdateRequestBody = (values: PersonEntityFormValues): ColumnUpdatePersonEntity => {
  const isCitizen = values.temporaryData?.isCitizen !== false;

  const firstName = trimValue(values.firstName);
  const middleName = trimValue(values.middleName);
  const lastName = trimValue(values.lastName);
  const ssn = isCitizen ? trimValue(values.ssn) : undefined;
  const dateOfBirth = normalizeDateOfBirth(values.dateOfBirth);
  const email = trimValue(values.email);
  const phoneNumber = trimValue(values.phoneNumber);
  const address = normalizeAddress(values.address);
  const passport = !isCitizen ? normalizeIdentification(values.passport) : undefined;
  const driversLicense = !isCitizen ? normalizeIdentification(values.driversLicense) : undefined;
  const nationalId = !isCitizen ? normalizeIdentification(values.nationalId) : undefined;

  const countryOfCitizenship = trimValue(values.countryOfCitizenship);
  const pepStatus = trimValue(values.pepStatus);
  const employmentStatus = trimValue(values.employmentStatus);
  const occupation = trimValue(values.occupation);
  const role = trimValue(values.role);
  const sourceOfIncome = normalizeStringList(values.sourceOfIncome);
  const income = normalizeNumberList(values.income);
  const purposeOfAccount = normalizeStringList(values.purposeOfAccount);
  const expectedPaymentRails = trimValue(values.expectedPaymentRails);

  return {
    first_name: firstName ?? '',
    last_name: lastName ?? '',
    ...(middleName && { middle_name: middleName }),
    ...(ssn && { ssn }),
    ...(dateOfBirth && { date_of_birth: dateOfBirth }),
    ...(email && { email }),
    ...(phoneNumber && { phone_number: phoneNumber }),
    ...(address && { address }),
    ...(passport && { passport }),
    ...(driversLicense && { drivers_license: driversLicense }),
    ...(nationalId && { national_id: nationalId }),
    ...(countryOfCitizenship && { country_of_citizenship: countryOfCitizenship }),
    ...(pepStatus && { pep_status: pepStatus }),
    ...(values.isHighRisk !== undefined && { is_high_risk: values.isHighRisk }),
    ...(employmentStatus && { employment_status: employmentStatus }),
    ...(occupation && { occupation }),
    ...(role && { role }),
    ...(sourceOfIncome && { source_of_income: sourceOfIncome }),
    ...(income && { income }),
    ...(purposeOfAccount && { purpose_of_account: purposeOfAccount }),
    ...(expectedPaymentRails && { expected_payment_rails: expectedPaymentRails }),
    ...(values.makesInternationalPayments !== undefined && {
      makes_international_payments: values.makesInternationalPayments,
    }),
  };
};

export const buildPersonUpdateRequest = (values: PersonEntityFormValues): ColumnUpdatePersonEntity =>
  buildPersonUpdateRequestBody(values);

export const buildPersonCreateRequest = (values: PersonEntityFormValues): ColumnCreatePersonEntity =>
  buildPersonUpdateRequestBody(values) as ColumnCreatePersonEntity;

// Builds the POST /entities/{entity_id}/associated-persons body for a linked person, or
// undefined when no person has been selected (so blank rows are skipped).
export const buildAssociatedPersonLinkRequest = (
  values: AssociatedPersonFormValues
): ColumnLinkAssociatedPersonRequest | undefined => {
  const personEntityId = trimValue(values.personEntityId);
  if (!personEntityId) return undefined;

  const titleInBusiness = trimValue(values.titleInBusiness);
  const hasOwnership =
    values.ownershipPercentage !== undefined &&
    !isNaN(values.ownershipPercentage) &&
    values.ownershipPercentage >= 0 &&
    values.ownershipPercentage <= 100;

  return {
    person_entity_id: personEntityId,
    roles: values.roles,
    ...(hasOwnership && { ownership_percentage: values.ownershipPercentage }),
    ...(titleInBusiness && { title_in_business: titleInBusiness }),
  };
};

// Builds the PUT /entities/{entity_id}/associated-persons body: the complete set of links the form
// should end up with. The endpoint reconciles against this list — upserting each entry (so edits to
// an existing person's roles/ownership/title persist) and removing any link not present — so the
// full list must be sent, not just newly added rows. Blank rows (no person selected) are dropped.
export const buildUpdateAssociatedPersonsRequest = (
  values: BusinessEntityFormValues
): ColumnUpdateAssociatedPersonsRequest => ({
  associated_persons: (values.associatedPersons ?? [])
    .map((owner) => buildAssociatedPersonLinkRequest(owner))
    .filter((body): body is NonNullable<typeof body> => body !== undefined),
});

const buildBusinessRequestBody = (values: BusinessEntityFormValues): ColumnUpdateBusinessEntity => {
  // Associated persons are linked separately via PUT /entities/{id}/associated-persons after
  // the business exists, so they are intentionally omitted from the business payload here.
  const businessName = trimValue(values.businessName);
  const ein = trimValue(values.ein);
  const website = trimValue(values.website);
  const address = normalizeAddress(values.address);

  const legalType = trimValue(values.legalType);
  const industry = trimValue(values.industry);
  const dbaName = trimValue(values.dbaName);
  const registrationId = normalizeIdentification(values.registrationId);
  const countryOfIncorporation = trimValue(values.countryOfIncorporation);
  const stateOfIncorporation = trimValue(values.stateOfIncorporation);
  const dateOfIncorporation = trimValue(values.dateOfIncorporation);
  const yearOfIncorporation = trimValue(values.yearOfIncorporation);
  const description = trimValue(values.description);
  const accountUsage = normalizeStringList(values.accountUsage);
  const purposeOfAccount = trimValue(values.purposeOfAccount);
  const paymentVolumes = trimValue(values.paymentVolumes);
  const countriesOfOperation = normalizeStringList(values.countriesOfOperation);
  const sourceOfFunds = trimValue(values.sourceOfFunds);
  const expectedUseOfFunds = trimValue(values.expectedUseOfFunds);
  const expectedPaymentRails = trimValue(values.expectedPaymentRails);
  const endCustomerType = trimValue(values.endCustomerType);

  return {
    business_name: businessName ?? '',
    ...(ein && { ein }),
    ...(website && { website }),
    address: address ?? createEmptyRequestAddress(),
    ...(legalType && { legal_type: legalType }),
    ...(industry && { industry }),
    ...(dbaName && { dba_name: dbaName }),
    ...(registrationId && { registration_id: registrationId }),
    ...(countryOfIncorporation && { country_of_incorporation: countryOfIncorporation }),
    ...(stateOfIncorporation && { state_of_incorporation: stateOfIncorporation }),
    ...(dateOfIncorporation && { date_of_incorporation: dateOfIncorporation }),
    ...(yearOfIncorporation && { year_of_incorporation: yearOfIncorporation }),
    ...(description && { description }),
    ...(accountUsage && { account_usage: accountUsage }),
    ...(purposeOfAccount && { purpose_of_account: purposeOfAccount }),
    ...(paymentVolumes && { payment_volumes: paymentVolumes }),
    ...(countriesOfOperation && { countries_of_operation: countriesOfOperation }),
    ...(sourceOfFunds && { source_of_funds: sourceOfFunds }),
    ...(expectedUseOfFunds && { expected_use_of_funds: expectedUseOfFunds }),
    ...(expectedPaymentRails && { expected_payment_rails: expectedPaymentRails }),
    ...(values.makesInternationalPayments !== undefined && {
      makes_international_payments: values.makesInternationalPayments,
    }),
    ...(values.hasVirtualAddress !== undefined && { has_virtual_address: values.hasVirtualAddress }),
    ...(endCustomerType && { end_customer_type: endCustomerType }),
    ...(values.isHighRisk !== undefined && { is_high_risk: values.isHighRisk }),
  };
};

// Which identification fields the user changed on this edit. Updates are PATCH-style, so anything
// omitted is left untouched server-side.
export interface ChangedBusinessIdentification {
  ein?: boolean;
  registrationId?: boolean;
}

export const buildBusinessUpdateRequest = (
  values: BusinessEntityFormValues,
  changed?: ChangedBusinessIdentification
): ColumnUpdateBusinessEntity => {
  const body = buildBusinessRequestBody(values);
  if (!changed) {
    return body;
  }
  // The API rejects a request carrying both an EIN and a registration ID (they're mutually
  // exclusive), yet the edit form loads both from the existing entity — and some businesses store
  // both. Since an update is a partial patch, drop the identification field the user didn't touch:
  // omitting it leaves its stored value intact and only the changed one is submitted.
  if (!changed.ein) {
    delete body.ein;
  }
  if (!changed.registrationId) {
    delete body.registration_id;
  }
  return body;
};

export const buildBusinessCreateRequest = (
  values: BusinessEntityFormValues,
  opts?: { allowEinPending?: boolean }
): ColumnCreateBusinessEntity => {
  const einPending = opts?.allowEinPending !== false && values.einPending === true;
  const request = buildBusinessRequestBody({
    ...values,
    ein: einPending ? '' : values.ein,
  });

  return {
    ...request,
    ...(einPending && { ein_pending: true as unknown as ColumnCreateBusinessEntity['ein_pending'] }),
  } as ColumnCreateBusinessEntity;
};

export const applyPersonDefaults = (values: Partial<PersonEntityFormValues>): PersonEntityFormValues => {
  const base = createDefaultPersonFormValues();
  const merged: PersonEntityFormValues = {
    ...base,
    ...values,
    address: { ...base.address, ...(values.address ?? {}) },
    passport: { ...base.passport, ...(values.passport ?? {}) },
    driversLicense: { ...base.driversLicense, ...(values.driversLicense ?? {}) },
    nationalId: { ...base.nationalId, ...(values.nationalId ?? {}) },
  };

  merged.temporaryData = { isCitizen: inferCitizenFromIdentifiers(merged) };

  return merged;
};

export const applyAssociatedPersonDefaults = (
  owner: Partial<AssociatedPersonFormValues>
): AssociatedPersonFormValues => ({
  ...createDefaultAssociatedPerson(),
  ...owner,
  roles: owner.roles ?? ['beneficial_owner'],
});

export const applyBusinessDefaults = (values: Partial<BusinessEntityFormValues>): BusinessEntityFormValues => {
  const base = createDefaultBusinessFormValues();
  const merged: BusinessEntityFormValues = {
    ...base,
    ...values,
    address: { ...base.address, ...(values.address ?? {}) },
    registrationId: { ...base.registrationId, ...(values.registrationId ?? {}) },
    associatedPersons:
      values.associatedPersons && values.associatedPersons.length > 0
        ? values.associatedPersons.map((owner) => applyAssociatedPersonDefaults(owner))
        : [],
  };

  return merged;
};

export const getPersonFormValuesFromEntity = (entity: ColumnEntity | null | undefined): PersonEntityFormValues => {
  if (!entity?.person_details) {
    return applyPersonDefaults(createDefaultPersonFormValues());
  }

  return applyPersonDefaults({
    ...apiPersonDetailsToFormValues(entity.person_details),
    id: entity.id,
  });
};

export const getBusinessFormValuesFromEntity = (entity: ColumnEntity | null | undefined): BusinessEntityFormValues => {
  if (!entity?.business_details) {
    return applyBusinessDefaults(createDefaultBusinessFormValues());
  }

  return applyBusinessDefaults({
    ...apiBusinessDetailsToFormValues(entity.business_details),
    id: entity.id,
  });
};

export type EntityFormValues = PersonEntityFormValues | BusinessEntityFormValues;

export const createDefaultValuesForType = (type: EntityFormType): EntityFormValues =>
  type === EntityFormType.Person ? createDefaultPersonFormValues() : createDefaultBusinessFormValues();

export const useEntityForm = (type: EntityFormType = EntityFormType.Person, defaultValues?: EntityFormValues) =>
  useForm<EntityFormValues>({
    mode: 'onChange',
    defaultValues: defaultValues ?? createDefaultValuesForType(type),
    shouldUnregister: false,
  });
